"use client";

import React from "react";
import { MapPin, Navigation, ArrowRight } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
const serviceRegions = [{
  county: "Summit County",
  description: "Comprehensive coverage including Akron, Cuyahoga Falls, Stow, Hudson, Fairlawn, Green, and Barberton. Rapid 30-minute response available for emergency tree and landscape services."
}, {
  county: "Stark County",
  description: "Serving the greater Canton area, Massillon, North Canton, Jackson Township, and Plain Township with elite residential and commercial landscaping solutions."
}, {
  county: "Cuyahoga County (South)",
  description: "Extending our premium services to southern Cleveland suburbs including Parma, Strongsville, Broadview Heights, and Brecksville."
}, {
  county: "Portage & Medina",
  description: "Dedicated routes covering Kent, Ravenna, Medina, Brunswick, and Wadsworth, ensuring consistent, top-tier property maintenance."
}];
export default function ServiceArea() {
  const headerReveal = useRevealOnScroll({
    threshold: 0.1
  });
  const mapReveal = useRevealOnScroll({
    threshold: 0.2
  });
  const gridReveal = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="servicearea" className="bg-background py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/subtle_topo_pattern.png)] bg-repeat"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {}
        <div ref={headerReveal.ref} className={`text-center mb-16 transition-all duration-1000 ${headerReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-primary/10 text-primary mb-6">
            <Navigation className="w-4 h-4" />
            <span className="text-sm font-bold tracking-wider uppercase font-sans">Coverage Map</span>
          </div>
          <h2 className="font-default text-4xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight text-foreground mb-6">
            Serving Your Community
          </h2>
          <p className="font-sans text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
            Hard Working Landscaping provides elite, top-tier property care across Northeast Ohio. Our strategic locations ensure a rapid response to your landscaping needs.
          </p>
        </div>

        {}
        <div ref={mapReveal.ref} className={`mb-20 transition-all duration-1000 delay-200 ${mapReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <div className="rounded-2xl overflow-hidden shadow-2xl border border-border/40 relative bg-card">
            <AspectRatio ratio={16 / 7} className="bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/northeast_ohio_high_contrast_service_map.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/northeast_ohio_high_contrast_service_map.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1673628/img/northeast_ohio_high_contrast_service_map.png-wvc-sizes" alt="High-contrast Service Area Map highlighting Akron, Canton, and Cleveland with clear city labels" className="object-cover w-full h-full" loading="lazy" />
              {}
              <div className="absolute inset-0 ring-1 ring-inset ring-foreground/10 rounded-2xl pointer-events-none"></div>
            </AspectRatio>
          </div>
        </div>

        {}
        <div ref={gridReveal.ref} className={`grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto transition-all duration-1000 delay-300 ${gridReveal.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {serviceRegions.map((region, i) => <Card key={i} data-index={i} className="bg-card border-border/50 shadow-lg hover:shadow-xl transition-all duration-300 group">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-3 text-2xl text-foreground font-default">
                  <div className="p-2 rounded-lg bg-primary/10 text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors duration-300">
                    <MapPin className="w-6 h-6" />
                  </div>
                  {region.county}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-sans text-muted-foreground leading-relaxed text-base">
                  {region.description}
                </p>
              </CardContent>
            </Card>)}
        </div>

        {}
        <div className="mt-16 text-center">
          <p className="font-sans text-muted-foreground mb-6">
            Don't see your city listed? We frequently accommodate special requests outside our primary radius.
          </p>
          <Button asChild size="lg" className="font-sans text-base h-12 px-8">
            <Link to="#">
              Request a Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </Button>
        </div>

      </div>
    </section>;
}